/**
 * Sharing a chase list: the ranked ten, a chase binder, or a master set.
 *
 * These three go through `share_targets` rather than through
 * `binders.share_token`, for one reason that decided it: the ranked ten is not
 * a binders row at all, it is ten `wish_list` rows, so there is no column to
 * hang a token on. A second per-table token column would also be a second
 * uniqueness island - two tables minting 12-character tokens independently can
 * land on the same string - and then a link would have to try tables in an
 * order and let the first match win. One table with the token as its primary
 * key means a token means exactly one thing.
 *
 * `binderShare.js` still owns PC binders and is untouched, so the links already
 * in the wild keep resolving through `get_shared_binder` exactly as before.
 * `get_shared_target` reads both.
 */

import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { supabase } from '../lib/supabase';
import { SHARE_ORIGIN } from './binderShare';

/** The kinds the database knows. Kept as constants so a typo is a build error. */
export const SHARE_KIND = {
  TOP_TEN: 'wish_top_ten',
  WISH_BINDER: 'wish_binder',
  MASTER_SET: 'master_set',
};

/**
 * The public URL for a token.
 *
 * /s/ rather than /b/, because a link that says "binder" and opens a wish list
 * is a small lie that somebody has to hold in their head for ever. /b/ still
 * resolves every kind, so nothing that has already been sent breaks.
 */
export function shareTargetUrl(token) {
  return `${SHARE_ORIGIN}/s/${token}`;
}

/** Mint or reuse the token. Throws if the caller does not own the surface. */
export async function ensureShareToken(kind, rowId) {
  const { data, error } = await supabase.rpc('ensure_share_token', {
    p_kind: kind,
    p_row_id: rowId,
  });
  if (error) throw error;
  return data;
}

/**
 * Did the server refuse because the list belongs to a child?
 *
 * The rule lives in the database - a kid's chase list stays inside the app -
 * so the app recognises that refusal rather than trying to predict it from a
 * profile flag it may not have loaded yet.
 */
export function isMinorShareBlocked(error) {
  return /kids chase lists/i.test(String(error?.message || ''));
}

export const MINOR_SHARE_MESSAGE = 'Chase lists for kids stay inside the app.';

// Closing the share sheet without picking anything is not a failure.
const cancelled = (e) => /cancel|abort/i.test(String(e?.message || e?.errorMessage || e?.name || ''));

/** Hand a url to the native sheet, the web sheet, or the clipboard, in that order. */
async function openSheet({ title, text, url, dialogTitle }) {
  if (Capacitor.isNativePlatform()) {
    try { await Share.share({ title, text, url, dialogTitle }); }
    catch (e) { if (!cancelled(e)) throw e; return { url, method: 'cancelled' }; }
    return { url, method: 'share' };
  }
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try { await navigator.share({ title, text, url }); }
    catch (e) { if (!cancelled(e)) throw e; return { url, method: 'cancelled' }; }
    return { url, method: 'share' };
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
  }
  return { url, method: 'clipboard' };
}

/** Share somebody's ranked ten. `ownerId` is their users.id. */
export async function shareTopTen(ownerId, ownerName) {
  const token = await ensureShareToken(SHARE_KIND.TOP_TEN, ownerId);
  const url = shareTargetUrl(token);
  return {
    ...(await openSheet({
      title: ownerName ? `${ownerName}'s chase cards on CardChase` : 'My chase cards on CardChase',
      text: 'These are the ten cards I am chasing on CardChase',
      url,
      dialogTitle: 'Share my chase cards',
    })),
    token,
  };
}

/** Share a chase binder. */
export async function shareWishBinder(binder) {
  const token = await ensureShareToken(SHARE_KIND.WISH_BINDER, binder.id);
  const url = shareTargetUrl(token);
  const name = binder?.name || 'chase binder';
  return {
    ...(await openSheet({
      title: `${name} on CardChase`,
      // "my Charizards binder binder" read badly. The word only goes on when
      // the name lacks it.
      text: /\bbinder\b/i.test(name)
        ? `Cards I am chasing: my ${name} on CardChase`
        : `Cards I am chasing: my ${name} binder on CardChase`,
      url,
      dialogTitle: 'Share chase binder',
    })),
    token,
  };
}

/** Share a master set or a sub set. */
export async function shareMasterSet(row) {
  const token = await ensureShareToken(SHARE_KIND.MASTER_SET, row.id);
  const url = shareTargetUrl(token);
  const name = row?.name || 'a master set';
  return {
    ...(await openSheet({
      title: `${name} on CardChase`,
      text: `My progress on ${name} on CardChase`,
      url,
      dialogTitle: 'Share this set',
    })),
    token,
  };
}

/**
 * Share whichever chase surface this row is, decided by its binder_type.
 *
 * A wish binder and a master set are the same table with a different type, so
 * the call sites hold one handler rather than branching on the way in.
 */
export async function shareWishSurface(row) {
  if (row?.binder_type === 'master') return shareMasterSet(row);
  return shareWishBinder(row);
}

/** What a share token points at: { kind, ... }, or null when it is not shared. */
export async function resolveSharedTarget(token) {
  const { data, error } = await supabase.rpc('get_shared_target', { p_token: token });
  if (error) throw error;
  return data || null;
}
